import { PiBellRingingBold, PiWarningBold } from 'react-icons/pi'

import { duration } from '@/insights/common/formatUtils'
import type { AssetState } from '@/types/asset-types'

import { severityPill } from './ActiveAlertsCard'
import DetailCard from './DetailCard'
import RowsTable, { type RowsTableColumn } from './RowsTable'
import { formatDate } from './utils'

export interface AlertsAlarmsPanelProps {
  asset: AssetState
}

type RaisedLocation = AssetState['activeAlarms'][number]['raisedLocation']

/** The geofence when the raise was inside one, else the address, else the coordinates. */
const formatLocation = (location: RaisedLocation) => {
  if (!location) return 'N/A'
  const { geofence, address, lat, lng } = location
  if (geofence?.name && address) return `${geofence.name} (${address})`
  return geofence?.name ?? address ?? `${lat.toFixed(4)}, ${lng.toFixed(4)}`
}

/** Active alarms, then active alerts: severity, what it is, when and where it was raised. */
export default function AlertsAlarmsPanel({ asset }: AlertsAlarmsPanelProps) {
  const timeColumns: RowsTableColumn[] = [
    { header: 'Raised', className: 'whitespace-nowrap' },
    { header: 'Duration', className: 'whitespace-nowrap' },
    { header: 'Raised Location' },
  ]

  return (
    <div className="flex flex-col gap-2">
      <DetailCard dense title="Active Alarms" icon={<PiBellRingingBold />}>
        <RowsTable
          columns={[{ header: 'Severity' }, { header: 'Code' }, { header: 'Description' }, ...timeColumns]}
          rows={asset.activeAlarms.map((alarm) => [
            severityPill(alarm.severity),
            <span className="font-bold">{alarm.code}</span>,
            alarm.description,
            formatDate(alarm.raisedTime),
            duration(alarm.raisedTime),
            formatLocation(alarm.raisedLocation),
          ])}
          empty="No Active Alarms"
        />
      </DetailCard>

      <DetailCard dense title="Active Alerts" icon={<PiWarningBold />}>
        <RowsTable
          columns={[{ header: 'Severity' }, { header: 'Alert' }, ...timeColumns]}
          rows={asset.activeAlerts.map((alert) => [
            severityPill(alert.severity),
            <span className="font-bold">{alert.name}</span>,
            formatDate(alert.raisedTime),
            duration(alert.raisedTime),
            formatLocation(alert.raisedLocation),
          ])}
          empty="No Active Alerts"
        />
      </DetailCard>
    </div>
  )
}
